import { z } from 'zod';
import {
  CLOSE_REASONS,
  EXECUTION_MODES,
  EXIT_KEYS,
  ORDER_PURPOSES,
  ORDER_SIDES,
  ORDER_STATUSES,
  ORDER_TYPES,
  SIGNAL_DIRECTIONS,
  SIGNAL_STATUSES,
  STRATEGY_KEYS,
  TRADE_STATUSES,
} from '../enums';
import {
  decimalStringSchema,
  jsonObjectSchema,
  paginationQuerySchema,
  uuidSchema,
} from './common';
import { symbolSchema } from './universe';

/** Trades, positions, orders and signals (API_DOCS.md — Trades, Positions, Signals). */

export const positionDtoSchema = z.object({
  tradeId: uuidSchema,
  symbol: symbolSchema,
  mode: z.enum(EXECUTION_MODES),
  qty: decimalStringSchema,
  avgEntry: decimalStringSchema,
  lastPrice: decimalStringSchema.nullable(),
  unrealizedPnl: decimalStringSchema.nullable(),
  unrealizedPnlPct: decimalStringSchema.nullable(),
  currentStop: decimalStringSchema.nullable(),
  currentTp: decimalStringSchema.nullable(),
  highWater: decimalStringSchema.nullable(),
  exitKey: z.enum(EXIT_KEYS),
  openedAt: z.string().datetime(),
  /** `avgEntry × qty` — what the held quantity cost, before fees. */
  entryValue: decimalStringSchema,
  /** Commission paid on this trade so far (USDT). */
  feesPaid: decimalStringSchema,
  /** Taker fee rate a market sell will pay, as a fraction (0.001 = 0.1%). */
  exitFeeRate: decimalStringSchema,
  /** Price at which selling everything nets zero after both commissions. */
  breakEven: decimalStringSchema.nullable(),
});
export type PositionDto = z.infer<typeof positionDtoSchema>;

export const candleQuerySchema = z.object({
  symbol: symbolSchema,
  timeframe: z.enum(['1m', '5m', '15m', '1h']).default('5m'),
  limit: z.coerce.number().int().min(10).max(1000).default(300),
});
export type CandleQuery = z.infer<typeof candleQuerySchema>;

/** One OHLC bar; `time` is the bar's open, in seconds since epoch (UTC). */
export interface CandleDto {
  readonly time: number;
  readonly open: string;
  readonly high: string;
  readonly low: string;
  readonly close: string;
}

export const orderDtoSchema = z.object({
  id: uuidSchema,
  mode: z.enum(EXECUTION_MODES),
  tradeId: uuidSchema,
  clientOrderId: z.string(),
  exchangeOrderId: z.string().nullable(),
  symbol: symbolSchema,
  side: z.enum(ORDER_SIDES),
  type: z.enum(ORDER_TYPES),
  purpose: z.enum(ORDER_PURPOSES),
  status: z.enum(ORDER_STATUSES),
  requestedQty: decimalStringSchema,
  filledQty: decimalStringSchema,
  avgFillPrice: decimalStringSchema.nullable(),
  feeAmount: decimalStringSchema.nullable(),
  feeCurrency: z.string().nullable(),
  submittedAt: z.string().datetime().nullable(),
  filledAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type OrderDto = z.infer<typeof orderDtoSchema>;

export const signalDtoSchema = z.object({
  id: uuidSchema,
  mode: z.enum(EXECUTION_MODES),
  strategyKey: z.enum(STRATEGY_KEYS),
  symbol: symbolSchema,
  direction: z.enum(SIGNAL_DIRECTIONS),
  confidence: decimalStringSchema,
  status: z.enum(SIGNAL_STATUSES),
  reason: jsonObjectSchema.nullable(),
  rejectionReason: z.string().nullable(),
  sentimentEventIds: z.array(uuidSchema),
  createdAt: z.string().datetime(),
});
export type SignalDto = z.infer<typeof signalDtoSchema>;

export const tradeDtoSchema = z.object({
  id: uuidSchema,
  mode: z.enum(EXECUTION_MODES),
  symbol: symbolSchema,
  strategyKey: z.enum(STRATEGY_KEYS),
  status: z.enum(TRADE_STATUSES),
  entryPrice: decimalStringSchema.nullable(),
  entryQty: decimalStringSchema.nullable(),
  remainingQty: decimalStringSchema.nullable(),
  exitAvgPrice: decimalStringSchema.nullable(),
  realizedPnl: decimalStringSchema.nullable(),
  realizedPnlPct: decimalStringSchema.nullable(),
  feesTotal: decimalStringSchema,
  confidence: decimalStringSchema.nullable(),
  closeReason: z.enum(CLOSE_REASONS).nullable(),
  openedAt: z.string().datetime().nullable(),
  closedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type TradeDto = z.infer<typeof tradeDtoSchema>;

export const tradeDetailSchema = tradeDtoSchema.extend({
  orders: z.array(orderDtoSchema),
  signal: signalDtoSchema.nullable(),
});
export type TradeDetail = z.infer<typeof tradeDetailSchema>;

export const tradeQuerySchema = paginationQuerySchema.extend({
  mode: z.enum(EXECUTION_MODES).optional(),
  status: z.enum(TRADE_STATUSES).optional(),
  symbol: symbolSchema.optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
});
export type TradeQuery = z.infer<typeof tradeQuerySchema>;

export const signalQuerySchema = z.object({
  status: z.enum(SIGNAL_STATUSES).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export type SignalQuery = z.infer<typeof signalQuerySchema>;

export const closePositionResponseSchema = z.object({
  status: z.literal('closing'),
});
export type ClosePositionResponse = z.infer<typeof closePositionResponseSchema>;
